import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Search } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { crmService } from "@/services/crmService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { TRANSACTION_VIEWS, TRANSACTION_TYPES, REPRESENTATION_SIDES, txTypeLabel, sideLabel, txStatusLabel } from "@/config/crmOptions";
import { fmtCents, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ALL = "__all__";
const statusClass = (s) => ({ closed: "bg-emerald-100 text-emerald-800", cancelled: "bg-secondary text-muted-foreground",
  under_contract: "bg-amber-100 text-amber-800", pending: "bg-amber-100 text-amber-800", active: "bg-brand-navy/10 text-brand-navy" }[s] || "bg-secondary text-muted-foreground");

export default function Transactions() {
  const navigate = useNavigate();
  const { openNewTransaction } = useCrmActions();
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [fType, setFType] = useState(ALL);
  const [fSide, setFSide] = useState(ALL);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { transactions } = await crmService.listTransactions({
        view, search: search || undefined,
        tx_type: fType === ALL ? undefined : fType,
        representation: fSide === ALL ? undefined : fSide,
        sort: "closing",
      });
      setRows(transactions);
    } catch { toast.error("Could not load transactions"); }
    finally { setLoading(false); }
  }, [view, search, fType, fSide]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="transactions-page">
        <PageHeader eyebrow="AgentHQ · Sell" title="Transactions"
          description="Track every active deal from contract to close."
          actions={<Button onClick={() => openNewTransaction()} data-testid="tx-new"><FileSignature className="h-4 w-4" /> New Transaction</Button>} />

        <div className="flex flex-wrap gap-1 border-b border-border" data-testid="tx-tabs">
          {TRANSACTION_VIEWS.map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`tx-tab-${v.key}`}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${view === v.key ? "border-brand-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {v.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-border px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client or property…" className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="tx-search" />
          </div>
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="w-[140px]" data-testid="tx-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All types</SelectItem>{TRANSACTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fSide} onValueChange={setFSide}>
            <SelectTrigger className="w-[140px]" data-testid="tx-filter-side"><SelectValue placeholder="Side" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All sides</SelectItem>{REPRESENTATION_SIDES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading transactions" /></div>
          : rows.length === 0 ? (
            <EmptyState title="Your deals will appear here."
              description="Add your first transaction when a client moves into an active deal."
              action={<Button className="mt-5" onClick={() => openNewTransaction()} data-testid="tx-empty-new"><FileSignature className="h-4 w-4" /> New Transaction</Button>} />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-card">
              <Table data-testid="tx-table">
                <TableHeader><TableRow className="bg-secondary/50">
                  <TableHead>Client</TableHead><TableHead>Property</TableHead><TableHead>Type</TableHead><TableHead>Side</TableHead>
                  <TableHead>Status</TableHead><TableHead>Price</TableHead><TableHead>Expected Closing</TableHead><TableHead>Est. Commission</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow key={t.id} className="cursor-pointer" data-testid={`tx-row-${t.id}`} onClick={() => navigate(`/transactions/${t.id}`)}>
                      <TableCell className="font-medium">{t.contact?.first_name} {t.contact?.last_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.property_address_line1}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{txTypeLabel(t.transaction_type)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{sideLabel(t.representation_side)}</TableCell>
                      <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>{txStatusLabel(t.status)}</span></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtCents(t.sale_price)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(t.expected_closing_date)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.commission ? fmtCents(t.commission.estimated_agent_net) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </div>
    </AppShell>
  );
}
